import './Modal.scss';
import { FC, memo, ReactNode, Fragment, HTMLAttributes, ButtonHTMLAttributes } from 'react';
import Draggable from 'react-draggable';
import { mergeClassNames } from '../../../utils/commonFunctions';
import useModalInteraction from './hooks/useModalInteraction';
import CustomToolTip from '../CustomToolTip';

interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
    open: boolean;
    pinned: boolean;
    disabled?: boolean;
    resizable?: boolean;
    minWidth?: number;
    minHeight?: number;
    className?: string;
    children: ReactNode;
}
interface ComponentProps extends HTMLAttributes<HTMLDivElement> {
    className?: string;
    children: React.ReactNode;
}
interface ActionProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    className?: string;
    disabled?: boolean;
    children: React.ReactNode;
    variant?: 'outline__button' | 'primary__button';
    title?: string;
}

export type PositionType = {
    height: number;
    width: number;
    top?: number
    left?: number
    right?: number
    bottom?: number
}

export type ModalModeType = "open" | "pinned" | "focus" | "close" | 'detached' | 'resize' | 'select_tab'

export type WindowSize = 'small' | 'medium' | 'large'
export type WindowConfig = {
    open: boolean
    detached: boolean
    windowSize: WindowSize
    position: PositionType
    pinned: boolean
    zIndex: number
}

export type WindowStateType = {
    tabs: {
        label: string
        value: string
        icon: ReactNode
    }[]
    open: boolean,
    pinned: boolean,
    zIndex: number,
    windowSize: WindowSize
    position: PositionType
    selected: string
    windows: Record<string, WindowConfig>
}

export const Modal: FC<ModalProps> = memo(({
    open,
    pinned,
    disabled = false,
    resizable = false,
    className = '',
    children,
    ...props
}) => {
    const {
        modalRef,
        dragHandlers,
        startResize,
    } = useModalInteraction({
        pinned,
        draggable: !disabled,
        resizable,
    });

    if (!open) return null;

    return (
        <Fragment>
            {!pinned &&
                <Draggable
                    nodeRef={modalRef}
                    disabled={pinned}
                    onStart={dragHandlers.onStart}
                    onStop={dragHandlers.onStop}
                    bounds="parent"
                >
                    <div
                        ref={modalRef}
                        className={mergeClassNames(
                            `custom__modal__container__wrapper ${open && 'active'}`,
                            className
                        )}
                        {...props}
                    >
                        {children}
                    </div>
                </Draggable>
            }
            {pinned &&
                <div
                    ref={modalRef}
                    className={mergeClassNames(
                        `custom__modal__container__wrapper ${open && 'active'}`,
                        className
                    )}
                    {...props}
                >
                    {children}

                    {resizable && (
                        <Fragment>
                            <div className="modal__resize__handle modal__resize__handle__nw" onMouseDown={startResize('nw')} />
                            <div className="modal__resize__handle modal__resize__handle__ne" onMouseDown={startResize('ne')} />
                            <div className="modal__resize__handle modal__resize__handle__sw" onMouseDown={startResize('sw')} />
                            <div className="modal__resize__handle modal__resize__handle__se" onMouseDown={startResize('se')} />

                            <div className="modal__resize__handle modal__resize__handle__n" onMouseDown={startResize('n')} />
                            <div className="modal__resize__handle modal__resize__handle__s" onMouseDown={startResize('s')} />
                            <div className="modal__resize__handle modal__resize__handle__e" onMouseDown={startResize('e')} />
                            <div className="modal__resize__handle modal__resize__handle__w" onMouseDown={startResize('w')} />
                        </Fragment>
                    )}
                </div>
            }
        </Fragment>
    );
});

Modal.displayName = 'Modal';


export const ModalHeader: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__header__container`, className)} {...props}>
            {children}
        </div>
    );
});
ModalHeader.displayName = 'ModalHeader';


export const HeaderTitleContainer: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__header__title__container`, className)} {...props}>
            {children}
        </div>
    );
});
HeaderTitleContainer.displayName = 'HeaderTitleContainer';


export const HeaderTitleIcon: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__header__title__icon`, className)} {...props}>
            {children}
        </div>
    );
});
HeaderTitleIcon.displayName = 'HeaderTitleIcon';


export const HeaderTitle: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__header__title__text`, className)} {...props}>
            {children}
        </div>
    );
});
HeaderTitle.displayName = 'HeaderTitle';


export const HeaderActionsContainer: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__header__actions__container`, className)} {...props}>
            {children}
        </div>
    );
});
HeaderActionsContainer.displayName = 'HeaderActionsContainer';


export const HeaderAction: FC<ActionProps> = memo(({ children, title = '', className = '', disabled, ...props }) => {
    return (
        <CustomToolTip title={title} disabled={disabled}>
            <button className={mergeClassNames(`custom__modal__header__action`, className)} disabled={disabled} {...props}>
                {children}
            </button>
        </CustomToolTip>
    );
});
HeaderAction.displayName = 'HeaderAction';

export const HeaderActionLabel: FC<{ label: string, className?: string }> = memo(({ label = '', className = '' }) => {
    return (
        <div className={mergeClassNames(`custom__modal__header__action__label`, className)}>
            <CustomToolTip title={label}>
                {label}
            </CustomToolTip>
        </div>
    );
});
HeaderActionLabel.displayName = 'HeaderActionLabel';


export const ModalBody: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__body__container`, className)} {...props}>
            {children}
        </div>
    );
});
ModalBody.displayName = 'ModalBody';


export const ModalFooter: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__footer__container`, className)} {...props}>
            {children}
        </div>
    );
});
ModalFooter.displayName = 'ModalFooter';


export const FooterAction: FC<ActionProps> = memo(({ children, className = '', variant, disabled, ...props }) => {
    return (
        <button className={mergeClassNames(`custom__modal__footer__action ${variant}`, className)} disabled={disabled} {...props}>
            {children}
        </button>
    );
});
FooterAction.displayName = 'FooterAction';


export const FooterActionIcon: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__modal__footer__action__icon`, className)} {...props}>
            {children}
        </div>
    );
});
FooterActionIcon.displayName = 'FooterActionIcon';